import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
// Homepage is eagerly imported so first paint has zero JS waterfall.
import Waitlist from "./pages/Waitlist";

// Secondary routes load on demand. Each becomes its own chunk, so visitors
// who land on the homepage do not download JS for /thank-you, /referral, etc.
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const NeurodiversePlay = lazy(() => import("./pages/NeurodiversePlay"));
const Why = lazy(() => import("./pages/Why"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Referral = lazy(() => import("./pages/Referral"));
const Privacy = lazy(() => import("./pages/Privacy"));
// Standalone parent-activity soft-launch landing pages. Mounted OUTSIDE
// the main Layout so they do not inherit the header, footer, waitlist
// dialog, mobile sticky CTA, demo pill, or urgency modal from the
// primary site journey. Two variants exist: signup (default) and login.
const PhilippinesLanding = lazy(() => import("./pages/PhilippinesLanding"));
const PhilippinesLandingLogin = lazy(() =>
    import("./pages/PhilippinesLandingLogin"),
);
const PhilippinesLandingLoginMomentToActivity = lazy(() =>
    import("./pages/PhilippinesLandingLoginMomentToActivity"),
);
const ParentActivitySimple = lazy(() =>
    import("./pages/ParentActivitySimple"),
);
const ParentActivityMomentToActivity = lazy(() =>
    import("./pages/ParentActivityMomentToActivity"),
);

const ScrollToTop = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        // Only re-runs on route or hash change. The inner timer + element
        // lookups are local to the effect closure and intentionally not deps.
        if (hash) {
            const id = hash.replace("#", "");
            const t = setTimeout(() => {
                const el = document.getElementById(id);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                } else {
                    window.scrollTo({ top: 0, behavior: "auto" });
                }
            }, 60);
            return () => clearTimeout(t);
        }
        window.scrollTo({ top: 0, behavior: "auto" });
    }, [pathname, hash]);
    return null;
};

// Soft, brand-neutral splash while a lazy route chunk loads. Kept tiny so
// it never feels like a "loading screen".
const RouteFallback = () => (
    <div
        aria-hidden="true"
        className="min-h-[60vh] flex items-center justify-center"
    >
        <span className="inline-flex h-6 w-6 rounded-full border-2 border-brand-coral/30 border-t-brand-coral animate-spin" />
    </div>
);

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <ScrollToTop />
                <Routes>
                    {/* Standalone parent-activity landing page. Sits
                        OUTSIDE the primary <Layout /> so nothing from the
                        pre-launch site (header, waitlist dialog, mobile
                        sticky CTA, demo pill, urgency modal, referral
                        flow) is rendered on this page. */}
                    <Route
                        path="/parent-activity/ph"
                        element={
                            <Suspense fallback={<RouteFallback />}>
                                <PhilippinesLanding />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/parent-activity/ph-login"
                        element={
                            <Suspense fallback={<RouteFallback />}>
                                <PhilippinesLandingLogin />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/parent-activity/ph-login-moment-to-activity"
                        element={
                            <Suspense fallback={<RouteFallback />}>
                                <PhilippinesLandingLoginMomentToActivity />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/parent-activity/simple-activity"
                        element={
                            <Suspense fallback={<RouteFallback />}>
                                <ParentActivitySimple />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/parent-activity/moment-to-activity"
                        element={
                            <Suspense fallback={<RouteFallback />}>
                                <ParentActivityMomentToActivity />
                            </Suspense>
                        }
                    />
                    <Route element={<Layout />}>
                        <Route path="/" element={<Waitlist />} />
                        <Route path="/waitlist" element={<Waitlist />} />
                        <Route
                            path="/how-it-works"
                            element={
                                <Suspense fallback={<RouteFallback />}>
                                    <HowItWorks />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/neurodiverse-play"
                            element={
                                <Suspense fallback={<RouteFallback />}>
                                    <NeurodiversePlay />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/why"
                            element={
                                <Suspense fallback={<RouteFallback />}>
                                    <Why />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/thank-you"
                            element={
                                <Suspense fallback={<RouteFallback />}>
                                    <ThankYou />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/referral"
                            element={
                                <Suspense fallback={<RouteFallback />}>
                                    <Referral />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/privacy"
                            element={
                                <Suspense fallback={<RouteFallback />}>
                                    <Privacy />
                                </Suspense>
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </div>
    );
}

export default App;
